import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
  NumberInput,
  NumberInputField,
  Select,
  Code,
} from "@chakra-ui/react";
import { EditIcon, AddIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";
import { OrderChannel } from "../constants/OrderChannel";
import {
  PLAN_ESSENTIAL,
  PLAN_STANDARD,
  PLAN_PRO_KITCHEN,
  PLAN_ELITE_KITCHEN,
  getAllPlans,
  getPlanDisplayName,
  getPlanPrice,
  getPlanFeatures,
} from "../../utils/subscription/planFeatures";

const PLANS_COLLECTION = "subscriptionPlans";

interface Plan {
  id: string;
  name: string;
  displayName: string;
  price: number;
  orderLimit: number | null; // null means unlimited
  features: OrderChannel[];
  supportsKDS: boolean;
  supportsReservations: boolean;
  supportsDelivery: boolean;
  supportsOnlineOrders: boolean;
  createdAt?: any;
  updatedAt?: any;
  updatedBy?: string;
}

const PlanManagement: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Form state
  const [planId, setPlanId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [price, setPrice] = useState(0);
  const [orderLimit, setOrderLimit] = useState<number | null>(100);
  const [selectedFeatures, setSelectedFeatures] = useState<OrderChannel[]>([]);
  const [supportsKDS, setSupportsKDS] = useState(false);
  const [supportsReservations, setSupportsReservations] = useState(false);
  const [supportsDelivery, setSupportsDelivery] = useState(false);
  const [supportsOnlineOrders, setSupportsOnlineOrders] = useState(false);

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const plansRef = collection(db, PLANS_COLLECTION);
      const snapshot = await getDocs(plansRef);
      
      if (snapshot.empty) {
        // Initialize with default plans if collection is empty
        await initializeDefaultPlans();
        await loadPlans(); // Reload after initialization
        return;
      }

      const plansData: Plan[] = [];
      snapshot.forEach((doc) => {
        plansData.push({ id: doc.id, ...doc.data() } as Plan);
      });
      
      // Sort by price
      plansData.sort((a, b) => a.price - b.price);
      setPlans(plansData);
    } catch (error) {
      console.error("Error loading plans:", error);
      toast({
        title: "Error",
        description: "Failed to load plans",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultPlans = async () => {
    const defaultPlans: Omit<Plan, "id" | "createdAt" | "updatedAt" | "updatedBy">[] = [
      {
        name: PLAN_ESSENTIAL,
        displayName: "Essential (Free Forever)",
        price: 0,
        orderLimit: 100,
        features: [OrderChannel.COUNTER],
        supportsKDS: false,
        supportsReservations: false,
        supportsDelivery: false,
        supportsOnlineOrders: false,
      },
      {
        name: PLAN_STANDARD,
        displayName: "Standard",
        price: 59,
        orderLimit: null,
        features: [OrderChannel.COUNTER, OrderChannel.TABLE, OrderChannel.TAKEAWAY],
        supportsKDS: false,
        supportsReservations: true,
        supportsDelivery: false,
        supportsOnlineOrders: false,
      },
      {
        name: PLAN_PRO_KITCHEN,
        displayName: "Pro Kitchen",
        price: 79,
        orderLimit: null,
        features: [OrderChannel.COUNTER, OrderChannel.TABLE, OrderChannel.TAKEAWAY, OrderChannel.DELIVERY],
        supportsKDS: true,
        supportsReservations: true,
        supportsDelivery: true,
        supportsOnlineOrders: false,
      },
      {
        name: PLAN_ELITE_KITCHEN,
        displayName: "Elite Kitchen",
        price: 169,
        orderLimit: null,
        features: [OrderChannel.COUNTER, OrderChannel.TABLE, OrderChannel.TAKEAWAY, OrderChannel.DELIVERY, OrderChannel.ONLINE],
        supportsKDS: true,
        supportsReservations: true,
        supportsDelivery: true,
        supportsOnlineOrders: true,
      },
    ];

    for (const plan of defaultPlans) {
      const planRef = doc(db, PLANS_COLLECTION, plan.name);
      await setDoc(planRef, {
        ...plan,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "system",
      });
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setPlanId(plan.id);
    setDisplayName(plan.displayName);
    setPrice(plan.price);
    setOrderLimit(plan.orderLimit);
    setSelectedFeatures([...plan.features]);
    setSupportsKDS(plan.supportsKDS);
    setSupportsReservations(plan.supportsReservations);
    setSupportsDelivery(plan.supportsDelivery);
    setSupportsOnlineOrders(plan.supportsOnlineOrders);
    setIsCreating(false);
    onOpen();
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setPlanId("");
    setDisplayName("");
    setPrice(0);
    setOrderLimit(100);
    setSelectedFeatures([OrderChannel.COUNTER]);
    setSupportsKDS(false);
    setSupportsReservations(false);
    setSupportsDelivery(false);
    setSupportsOnlineOrders(false);
    setIsCreating(true);
    onOpen();
  };

  const handleFeatureToggle = (feature: OrderChannel) => {
    if (selectedFeatures.includes(feature)) {
      setSelectedFeatures(selectedFeatures.filter((f) => f !== feature));
    } else {
      setSelectedFeatures([...selectedFeatures, feature]);
    }
  };

  const handleSave = async () => {
    if (!planId.trim() || !displayName.trim()) {
      toast({
        title: "Validation Error",
        description: "Plan ID and Display Name are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (price < 0) {
      toast({
        title: "Validation Error",
        description: "Price cannot be negative",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const planData: Omit<Plan, "id"> = {
        name: planId.toLowerCase().replace(/\s+/g, "_"),
        displayName: displayName.trim(),
        price: price,
        orderLimit: orderLimit,
        features: selectedFeatures,
        supportsKDS: supportsKDS,
        supportsReservations: supportsReservations,
        supportsDelivery: supportsDelivery,
        supportsOnlineOrders: supportsOnlineOrders,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };

      if (isCreating) {
        planData.createdAt = serverTimestamp();
      }

      const planRef = doc(db, PLANS_COLLECTION, planData.name);
      await setDoc(planRef, planData, { merge: true });

      toast({
        title: "Success",
        description: isCreating ? "Plan created successfully" : "Plan updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
      loadPlans();
    } catch (error: any) {
      console.error("Error saving plan:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save plan",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const orderChannelLabels: { [key in OrderChannel]: string } = {
    [OrderChannel.COUNTER]: "Counter",
    [OrderChannel.TABLE]: "Table Service",
    [OrderChannel.TAKEAWAY]: "Takeaway",
    [OrderChannel.DELIVERY]: "Delivery",
    [OrderChannel.ONLINE]: "Online Orders",
  };

  return (
    <PageShell
      title="Plan Management"
      description="Prices and features offered to stores."
      wide
      actions={
        <Button leftIcon={<AddIcon />} onClick={handleCreate}>
          Create New Plan
        </Button>
      }
    >
      <VStack spacing={6} align="stretch">

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor}>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Plan ID</Th>
                  <Th>Display Name</Th>
                  <Th>Price</Th>
                  <Th>Order Limit</Th>
                  <Th>Features</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      Loading...
                    </Td>
                  </Tr>
                ) : plans.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      No plans found
                    </Td>
                  </Tr>
                ) : (
                  plans.map((plan) => (
                    <Tr key={plan.id}>
                      <Td>
                        <Code>{plan.name}</Code>
                      </Td>
                      <Td fontWeight="semibold">{plan.displayName}</Td>
                      <Td>
                        ${plan.price}
                        {plan.price > 0 && "/mo"}
                      </Td>
                      <Td>
                        {plan.orderLimit === null ? (
                          <Badge colorScheme="green">Unlimited</Badge>
                        ) : (
                          `${plan.orderLimit}/day`
                        )}
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          {plan.features.map((feature) => (
                            <Badge key={feature} colorScheme="blue" fontSize="xs">
                              {orderChannelLabels[feature]}
                            </Badge>
                          ))}
                          {plan.supportsKDS && (
                            <Badge colorScheme="purple" fontSize="xs">
                              KDS
                            </Badge>
                          )}
                          {plan.supportsReservations && (
                            <Badge colorScheme="orange" fontSize="xs">
                              Reservations
                            </Badge>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <IconButton
                          aria-label="Edit plan"
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEdit(plan)}
                        />
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* Edit/Create Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{isCreating ? "Create New Plan" : "Edit Plan"}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Plan ID</FormLabel>
                  <Input
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    placeholder="essential"
                    isDisabled={!isCreating}
                    bg="white"
                  />
                  <Text fontSize="xs" color={textSecondary} mt={1}>
                    Lowercase, use underscores (e.g., "pro_kitchen")
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Display Name</FormLabel>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Essential (Free Forever)"
                    bg="white"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Price (USD/month)</FormLabel>
                  <NumberInput value={price} onChange={(_, val) => setPrice(isNaN(val) ? 0 : val)} min={0}>
                    <NumberInputField bg="white" />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Order Limit</FormLabel>
                  <Select
                    value={orderLimit === null ? "unlimited" : "limited"}
                    onChange={(e) => setOrderLimit(e.target.value === "unlimited" ? null : 100)}
                    bg="white"
                  >
                    <option value="unlimited">Unlimited</option>
                    <option value="limited">Limited</option>
                  </Select>
                  {orderLimit !== null && (
                    <NumberInput
                      value={orderLimit}
                      onChange={(_, val) => setOrderLimit(isNaN(val) ? 100 : val)}
                      min={1}
                      mt={2}
                    >
                      <NumberInputField bg="white" placeholder="100" />
                    </NumberInput>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Order Channels</FormLabel>
                  <VStack align="start" spacing={2}>
                    {Object.entries(orderChannelLabels).map(([channel, label]) => (
                      <Checkbox
                        key={channel}
                        isChecked={selectedFeatures.includes(channel as OrderChannel)}
                        onChange={() => handleFeatureToggle(channel as OrderChannel)}
                      >
                        {label}
                      </Checkbox>
                    ))}
                  </VStack>
                </FormControl>

                <FormControl>
                  <FormLabel>Additional Features</FormLabel>
                  <VStack align="start" spacing={2}>
                    <Checkbox
                      isChecked={supportsKDS}
                      onChange={(e) => setSupportsKDS(e.target.checked)}
                    >
                      KDS (Kitchen Display System)
                    </Checkbox>
                    <Checkbox
                      isChecked={supportsReservations}
                      onChange={(e) => setSupportsReservations(e.target.checked)}
                    >
                      Reservations
                    </Checkbox>
                    <Checkbox
                      isChecked={supportsDelivery}
                      onChange={(e) => setSupportsDelivery(e.target.checked)}
                    >
                      Delivery
                    </Checkbox>
                    <Checkbox
                      isChecked={supportsOnlineOrders}
                      onChange={(e) => setSupportsOnlineOrders(e.target.checked)}
                    >
                      Online Orders
                    </Checkbox>
                  </VStack>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSave}>
                {isCreating ? "Create" : "Save"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </PageShell>
  );
};

export default PlanManagement;

